import { useEffect, useState } from 'react';
import { GOOGLE_PLACES_API_KEY } from 'constants/envConstants';

type ScriptStatus = 'loading' | 'ready' | 'error';

const SCRIPT_LOAD_TIMEOUT = 10000; // 10 seconds

export const useGoogleMapsScript = (): ScriptStatus => {
  const [status, setStatus] = useState<ScriptStatus>('loading');

  useEffect(() => {
    if (!GOOGLE_PLACES_API_KEY) {
      setStatus('error');
      return undefined;
    }

    // Check if Google Maps is already loaded
    const checkGoogleMapsReady = () =>
      !!(window as { google?: { maps?: { places?: unknown } } }).google?.maps
        ?.places;

    if (checkGoogleMapsReady()) {
      setStatus('ready');
      return undefined;
    }

    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com/maps/api/js"]'
    ) as HTMLScriptElement | null;

    let timeoutId: NodeJS.Timeout;
    let pollIntervalId: NodeJS.Timeout;

    // Poll for Google Maps to become available (handles race conditions)
    const startPolling = () => {
      pollIntervalId = setInterval(() => {
        if (checkGoogleMapsReady()) {
          clearInterval(pollIntervalId);
          clearTimeout(timeoutId);
          setStatus('ready');
        }
      }, 100);
    };

    // Set up timeout to detect hanging scripts
    timeoutId = setTimeout(() => {
      clearInterval(pollIntervalId);
      if (!checkGoogleMapsReady()) {
        setStatus('error');
      }
    }, SCRIPT_LOAD_TIMEOUT);

    if (existingScript) {
      // Script exists but not loaded yet
      // Check if script is already complete
      const scriptReadyState = (
        existingScript as HTMLScriptElement & { readyState?: string }
      ).readyState;
      if (scriptReadyState === 'complete' || scriptReadyState === 'loaded') {
        // Script finished loading, but Google Maps might not be ready yet
        startPolling();
        return () => {
          clearTimeout(timeoutId);
          clearInterval(pollIntervalId);
        };
      }
      // Script is still loading, add event listeners
      const handleExistingLoad = () => {
        clearTimeout(timeoutId);
        // Give it a moment for the library to initialize
        setTimeout(() => {
          if (checkGoogleMapsReady()) {
            clearInterval(pollIntervalId);
            setStatus('ready');
          } else {
            startPolling();
          }
        }, 100);
      };
      const handleExistingError = () => {
        clearTimeout(timeoutId);
        clearInterval(pollIntervalId);
        setStatus('error');
      };
      existingScript.addEventListener('load', handleExistingLoad);
      existingScript.addEventListener('error', handleExistingError);

      // Also start polling as a fallback
      startPolling();

      return () => {
        clearTimeout(timeoutId);
        clearInterval(pollIntervalId);
        existingScript.removeEventListener('load', handleExistingLoad);
        existingScript.removeEventListener('error', handleExistingError);
      };
    }

    // Create new script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_PLACES_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;

    const handleLoad = () => {
      clearTimeout(timeoutId);
      // Give it a moment for the library to initialize
      setTimeout(() => {
        if (checkGoogleMapsReady()) {
          clearInterval(pollIntervalId);
          setStatus('ready');
        } else {
          startPolling();
        }
      }, 100);
    };

    const handleError = () => {
      clearTimeout(timeoutId);
      clearInterval(pollIntervalId);
      // eslint-disable-next-line no-console
      console.error('Google Maps script failed to load');
      setStatus('error');
    };

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);

    document.head.appendChild(script);

    // Start polling as a fallback
    startPolling();

    return function cleanup() {
      clearTimeout(timeoutId);
      clearInterval(pollIntervalId);
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };
  }, []);

  return status;
};
